import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { Class } from '../types'
import {
  CreateClassRequest,
  getClasses,
  createClass,
  updateClass,
  deleteClass,
  updateStudentClasses,
} from '../services/classes'

interface ClassState {
  // State for classes in user's schools (for admin/manage views)
  schoolClasses: Class[]
  schoolClassesLoading: boolean
  schoolClassesError: string | null

  // State for classes where user is a teacher (for teacher views)
  teacherClasses: Class[]
  teacherClassesLoading: boolean
  teacherClassesError: string | null

  // General state for create/update/delete operations
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean

  // Actions
  fetchSchoolClasses: () => Promise<void>
  fetchTeacherClasses: () => Promise<void>
  addClass: (classData: CreateClassRequest) => Promise<Class>
  editClass: (
    id: string,
    classData: Partial<CreateClassRequest>,
  ) => Promise<Class>
  removeClass: (id: string) => Promise<void>
  updateStudentClasses: (studentId: string, classIds: string[]) => Promise<void>
  clearSchoolClassesError: () => void
  clearTeacherClassesError: () => void
}

export const useClassStore = create<ClassState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        schoolClasses: [],
        schoolClassesLoading: false,
        schoolClassesError: null,
        teacherClasses: [],
        teacherClassesLoading: false,
        teacherClassesError: null,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,

        fetchSchoolClasses: async () => {
          try {
            set({ schoolClassesLoading: true, schoolClassesError: null })
            const classes = await getClasses()
            set({
              schoolClasses: classes,
              schoolClassesLoading: false,
            })
          } catch (error: any) {
            console.error('Error fetching school classes:', error)
            set({
              schoolClassesError:
                error.response?.data?.error || 'Failed to fetch school classes',
              schoolClassesLoading: false,
            })
          }
        },

        fetchTeacherClasses: async () => {
          try {
            set({ teacherClassesLoading: true, teacherClassesError: null })
            const classes = await getClasses()
            set({
              teacherClasses: classes,
              teacherClassesLoading: false,
            })
          } catch (error: any) {
            console.error('Error fetching teacher classes:', error)
            set({
              teacherClassesError:
                error.response?.data?.error ||
                'Failed to fetch teacher classes',
              teacherClassesLoading: false,
            })
          }
        },

        addClass: async (classData: CreateClassRequest) => {
          try {
            set({
              isCreating: true,
              schoolClassesError: null,
              teacherClassesError: null,
            })
            const newClass = await createClass(classData)

            // Add the new class to school classes (since create happens in manage context)
            set((state) => ({
              schoolClasses: [...state.schoolClasses, newClass],
              isCreating: false,
            }))
            return newClass
          } catch (error: any) {
            console.error('Error creating class:', error)
            set({
              schoolClassesError:
                error.response?.data?.error || 'Failed to create class',
              isCreating: false,
            })
            throw error
          }
        },

        editClass: async (
          id: string,
          classData: Partial<CreateClassRequest>,
        ) => {
          try {
            set({
              isUpdating: true,
              schoolClassesError: null,
              teacherClassesError: null,
            })
            const updatedClass = await updateClass({ _id: id, ...classData })

            // Update the class in both states
            set((state) => ({
              schoolClasses: state.schoolClasses.map((cls) =>
                cls._id === id ? updatedClass : cls,
              ),
              teacherClasses: state.teacherClasses.map((cls) =>
                cls._id === id ? updatedClass : cls,
              ),
              isUpdating: false,
            }))
            return updatedClass
          } catch (error: any) {
            console.error('Error updating class:', error)
            set({
              schoolClassesError:
                error.response?.data?.error || 'Failed to update class',
              isUpdating: false,
            })
            throw error
          }
        },

        removeClass: async (id: string) => {
          try {
            set({
              isDeleting: true,
              schoolClassesError: null,
              teacherClassesError: null,
            })
            await deleteClass(id)

            // Remove the class from both states
            set((state) => ({
              schoolClasses: state.schoolClasses.filter(
                (cls) => cls._id !== id,
              ),
              teacherClasses: state.teacherClasses.filter(
                (cls) => cls._id !== id,
              ),
              isDeleting: false,
            }))
          } catch (error: any) {
            console.error('Error deleting class:', error)
            set({
              schoolClassesError:
                error.response?.data?.error || 'Failed to delete class',
              isDeleting: false,
            })
            throw error
          }
        },

        updateStudentClasses: async (studentId: string, classIds: string[]) => {
          try {
            set({ schoolClassesError: null, teacherClassesError: null })
            await updateStudentClasses(studentId, classIds)

            // Refresh both class lists to get updated student lists
            const [schoolClasses, teacherClasses] = await Promise.all([
              getClasses(),
              getClasses(),
            ])
            set({
              schoolClasses,
              teacherClasses,
            })
          } catch (error: any) {
            console.error('Error updating student classes:', error)
            set({
              schoolClassesError:
                error.response?.data?.error ||
                'Failed to update student classes',
            })
            throw error
          }
        },

        clearSchoolClassesError: () => set({ schoolClassesError: null }),
        clearTeacherClassesError: () => set({ teacherClassesError: null }),
      }),
      {
        name: 'class-store',
      },
    ),
  ),
)
